/**
 * Hooks for the per-video verdict mutations the parent /feedback
 * card-list view drives. Both already exist on `mobile.graphql`
 * (the mobile review-deck uses them); this file just wires them
 * up for the web.
 *
 * - `useScoreVideo`     fires `scoreVideo(profileId, videoId, verdict)`
 *                       — same call the swipe deck makes when a parent
 *                       commits an Approve / Block decision.
 * - `useRetractVerdict` fires `retractVerdict(profileId, videoId)`
 *                       — Group 2 swipe-deck spec mutation. The
 *                       `RetractResult.partial` flag indicates a
 *                       downstream side-effect (channel block, recommendation
 *                       suppression) couldn't be undone; the UI shows the
 *                       partial-revert toast in that case.
 */
import {ApolloClient, gql, NormalizedCacheObject} from '@apollo/client';
import {useMutation} from 'react-query';

export interface RetractResult {
  success: boolean;
  partial?: boolean | null;
  note?: string | null;
  message?: string | null;
}

export interface ScoreVideoResult {
  success: boolean;
  message: string;
}

const RETRACT_VERDICT = gql`
  mutation RetractVerdict($profileId: String!, $videoId: String!) {
    retractVerdict(profileId: $profileId, videoId: $videoId) {
      success
      partial
      note
      message
    }
  }
`;

export function useRetractVerdict(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(
    async (vars: {profileId: string; videoId: string}): Promise<RetractResult> => {
      const {data} = await client.mutate<{retractVerdict: RetractResult}>({
        mutation: RETRACT_VERDICT,
        variables: vars,
      });
      if (!data?.retractVerdict) {
        throw new Error('retractVerdict returned no data');
      }
      return data.retractVerdict;
    },
  );
}

const SCORE_VIDEO = gql`
  mutation ScoreVideo($profileId: String!, $videoId: String!, $verdict: String!) {
    scoreVideo(profileId: $profileId, videoId: $videoId, verdict: $verdict) {
      success
      message
    }
  }
`;

export function useScoreVideo(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation(
    async (vars: {profileId: string; videoId: string; verdict: string}): Promise<ScoreVideoResult> => {
      const {data} = await client.mutate<{scoreVideo: ScoreVideoResult}>({
        mutation: SCORE_VIDEO,
        variables: vars,
      });
      if (!data?.scoreVideo) {
        throw new Error('scoreVideo returned no data');
      }
      return data.scoreVideo;
    },
  );
}
